<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import VaultSageAPI from 'dashboard/api/vaultsage';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const DEFAULT_BASE_URL = 'https://api.vaultsage.ai/api/v1';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { isCloudFeatureEnabled, accountScopedRoute } = useAccount();

const agentBots = useMapGetter('agentBots/getBots');
const isCheckingInboxes = ref(false);

const integrationLoaded = ref(false);
const isSaving = ref(false);
const isValidating = ref(false);
const isApiKeyVisible = ref(false);

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'vaultsage'
);
const uiFlags = useMapGetter('integrations/getUIFlags');

const isSageIntegrationEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.SAGE)
);

const formState = reactive({
  apiKey: '',
  baseUrl: DEFAULT_BASE_URL,
});

const connectedHook = computed(() => integration.value?.hooks?.[0] || null);
const isConnected = computed(() => !!connectedHook.value);
const connectedProfile = computed(() => connectedHook.value?.settings || {});

const agentBotInbox = computed(() => store.state.agentBots.agentBotInbox);
const agentBotRecords = computed(() => store.state.agentBots.records);

const hasVaultSageBot = computed(() =>
  agentBots.value.some(bot => bot.bot_type === 'vaultsage')
);

const hasVaultSageBotOnInbox = computed(() =>
  Object.entries(agentBotInbox.value).some(([, botId]) => {
    if (!botId) return false;
    const bot = agentBotRecords.value.find(b => Number(b.id) === Number(botId));
    return bot?.bot_type === 'vaultsage';
  })
);

const step1Complete = computed(() => isConnected.value);
const step2Complete = computed(() => hasVaultSageBot.value);
const step3Complete = computed(() => hasVaultSageBotOnInbox.value);
const apiKeyInputType = computed(() =>
  isApiKeyVisible.value ? 'text' : 'password'
);
const showApiKeyLabel = computed(() =>
  t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.SHOW')
);
const hideApiKeyLabel = computed(() =>
  t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.HIDE')
);
const apiKeyVisibilityLabel = computed(() =>
  isApiKeyVisible.value ? hideApiKeyLabel.value : showApiKeyLabel.value
);

watch(
  connectedHook,
  hook => {
    if (hook?.settings?.base_url) {
      formState.baseUrl = hook.settings.base_url;
    }
  },
  { immediate: true }
);

const refreshIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

const refreshSageSetupState = async () => {
  if (!isSageIntegrationEnabled.value) return;
  await store.dispatch('agentBots/get');
  await store.dispatch('inboxes/get');
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  if (!inboxes.length) return;
  isCheckingInboxes.value = true;
  try {
    await Promise.allSettled(
      inboxes.map(inbox =>
        store.dispatch('agentBots/fetchAgentBotInbox', inbox.id)
      )
    );
  } finally {
    isCheckingInboxes.value = false;
  }
};

const goToAgentBots = () => {
  router.push(accountScopedRoute('agent_bots'));
};

const goToInboxes = () => {
  router.push(accountScopedRoute('settings_inbox_list'));
};

const toggleApiKeyVisibility = () => {
  isApiKeyVisible.value = !isApiKeyVisible.value;
};

const validateConnection = async () => {
  if (!formState.apiKey) {
    useAlert(t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.REQUIRED'));
    return;
  }

  isValidating.value = true;
  try {
    const {
      data: { profile },
    } = await VaultSageAPI.validate({
      api_key: formState.apiKey,
      base_url: formState.baseUrl,
    });

    useAlert(
      t('INTEGRATION_SETTINGS.VAULTSAGE.VALIDATE.SUCCESS', {
        email:
          profile?.email || t('INTEGRATION_SETTINGS.VAULTSAGE.UNKNOWN_USER'),
      })
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.VAULTSAGE.VALIDATE.ERROR')
    );
  } finally {
    isValidating.value = false;
  }
};

const saveConnection = async () => {
  if (!formState.apiKey) {
    useAlert(t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    if (isConnected.value) {
      await VaultSageAPI.updateConnection({
        api_key: formState.apiKey,
        base_url: formState.baseUrl,
      });
    } else {
      await VaultSageAPI.connect({
        api_key: formState.apiKey,
        base_url: formState.baseUrl,
      });
    }

    formState.apiKey = '';
    await refreshIntegration();
    await refreshSageSetupState();
    useAlert(t('INTEGRATION_SETTINGS.VAULTSAGE.SAVE.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.VAULTSAGE.SAVE.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const disconnect = async () => {
  isSaving.value = true;
  try {
    await VaultSageAPI.disconnect();
    formState.apiKey = '';
    await refreshIntegration();
    await refreshSageSetupState();
    useAlert(t('INTEGRATION_SETTINGS.VAULTSAGE.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.VAULTSAGE.DISCONNECT.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

watch(isConnected, async connected => {
  if (connected && isSageIntegrationEnabled.value) {
    await refreshSageSetupState();
  }
});

onMounted(async () => {
  if (isSageIntegrationEnabled.value) {
    await refreshIntegration();
    if (isConnected.value) {
      await refreshSageSetupState();
    }
  } else {
    integrationLoaded.value = true;
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="
      isSageIntegrationEnabled && (!integrationLoaded || uiFlags.isFetching)
    "
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.VAULTSAGE.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.VAULTSAGE.PAGE_DESCRIPTION')"
        feature-name="integrations"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div
        v-if="!isSageIntegrationEnabled"
        class="mx-6 max-w-4xl rounded-xl bg-n-card outline outline-1 outline-n-container p-8"
      >
        <h3 class="text-heading-1 text-n-slate-12 mb-2">
          {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.FEATURE_DISABLED.TITLE') }}
        </h3>
        <p class="text-body-main text-n-slate-11">
          {{
            $t('INTEGRATION_SETTINGS.VAULTSAGE.FEATURE_DISABLED.DESCRIPTION')
          }}
        </p>
      </div>
      <div v-else class="mx-6 max-w-4xl flex flex-col gap-6">
        <div
          class="rounded-xl bg-n-card outline outline-1 outline-n-container p-6"
        >
          <div class="flex flex-col gap-2 mb-4">
            <h3 class="text-heading-1 text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.INTRO') }}
            </p>
          </div>
          <p
            v-if="isCheckingInboxes"
            class="text-sm text-n-slate-10 mb-3 flex items-center gap-2"
          >
            <Icon icon="i-lucide-loader-2" class="size-4 animate-spin" />
            {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.CHECKING_INBOXES') }}
          </p>
          <ul class="list-none m-0 p-0 flex flex-col gap-4">
            <li class="flex gap-3">
              <Icon
                :icon="
                  step1Complete ? 'i-lucide-circle-check' : 'i-lucide-circle'
                "
                class="size-5 shrink-0 mt-0.5"
                :class="step1Complete ? 'text-n-teal-11' : 'text-n-slate-9'"
              />
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.STEP1_TITLE') }}
                </p>
                <p class="text-sm text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.STEP1_DESC') }}
                </p>
              </div>
            </li>
            <li class="flex gap-3">
              <Icon
                :icon="
                  step2Complete ? 'i-lucide-circle-check' : 'i-lucide-circle'
                "
                class="size-5 shrink-0 mt-0.5"
                :class="step2Complete ? 'text-n-teal-11' : 'text-n-slate-9'"
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.STEP2_TITLE') }}
                </p>
                <p class="text-sm text-n-slate-11 mb-2">
                  {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.STEP2_DESC') }}
                </p>
                <Button
                  v-if="step1Complete"
                  size="sm"
                  :label="
                    $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.ACTION_CREATE_BOT')
                  "
                  @click="goToAgentBots"
                />
              </div>
            </li>
            <li class="flex gap-3">
              <Icon
                :icon="
                  step3Complete ? 'i-lucide-circle-check' : 'i-lucide-circle'
                "
                class="size-5 shrink-0 mt-0.5"
                :class="step3Complete ? 'text-n-teal-11' : 'text-n-slate-9'"
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.STEP3_TITLE') }}
                </p>
                <p class="text-sm text-n-slate-11 mb-2">
                  {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.STEP3_DESC') }}
                </p>
                <Button
                  v-if="step2Complete"
                  size="sm"
                  faded
                  slate
                  :label="
                    $t(
                      'INTEGRATION_SETTINGS.VAULTSAGE.SETUP.ACTION_OPEN_INBOXES'
                    )
                  "
                  @click="goToInboxes"
                />
              </div>
            </li>
          </ul>
        </div>

        <div
          class="rounded-xl bg-n-card outline outline-1 outline-n-container p-6"
        >
          <div class="flex flex-col gap-2 mb-6">
            <h3 class="text-heading-1 text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.CONNECT.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.CONNECT.DESCRIPTION') }}
            </p>
          </div>

          <div class="flex flex-col gap-4">
            <Input
              v-model="formState.baseUrl"
              :label="$t('INTEGRATION_SETTINGS.VAULTSAGE.BASE_URL.LABEL')"
              :placeholder="
                $t('INTEGRATION_SETTINGS.VAULTSAGE.BASE_URL.PLACEHOLDER')
              "
            />
            <Input
              v-model="formState.apiKey"
              :type="apiKeyInputType"
              :label="$t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.LABEL')"
              :placeholder="
                $t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.PLACEHOLDER')
              "
              custom-input-class="ltr:pr-10 rtl:pl-10"
            >
              <template #suffix>
                <button
                  type="button"
                  class="absolute bottom-2.5 ltr:right-3 rtl:left-3 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  :aria-label="apiKeyVisibilityLabel"
                  :title="apiKeyVisibilityLabel"
                  @click="toggleApiKeyVisibility"
                >
                  <Icon
                    :icon="
                      isApiKeyVisible ? 'i-lucide-eye-off' : 'i-lucide-eye'
                    "
                    class="size-4"
                  />
                </button>
              </template>
            </Input>
            <p class="text-sm text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.API_KEY.HINT') }}
            </p>

            <div class="flex flex-wrap gap-2 justify-end">
              <Button
                faded
                slate
                :label="$t('INTEGRATION_SETTINGS.VAULTSAGE.VALIDATE.BUTTON')"
                :is-loading="isValidating"
                @click="validateConnection"
              />
              <Button
                :label="$t('INTEGRATION_SETTINGS.VAULTSAGE.SAVE.BUTTON')"
                :is-loading="isSaving"
                @click="saveConnection"
              />
            </div>
          </div>
        </div>

        <div
          v-if="isConnected"
          class="rounded-xl bg-n-card outline outline-1 outline-n-container p-6"
        >
          <div class="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 class="text-heading-1 text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.TITLE') }}
              </h3>
              <p class="text-body-main text-n-slate-11">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.DESCRIPTION') }}
              </p>
            </div>
            <Button
              faded
              ruby
              :label="$t('INTEGRATION_SETTINGS.VAULTSAGE.DISCONNECT.BUTTON')"
              :is-loading="isSaving"
              @click="disconnect"
            />
          </div>

          <div
            class="rounded-lg bg-n-alpha-2 p-4 mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.ACTIONS_TITLE') }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.ACTIONS_HINT') }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2 shrink-0">
              <Button
                size="sm"
                :label="
                  $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.ACTION_CREATE_BOT')
                "
                @click="goToAgentBots"
              />
              <Button
                size="sm"
                faded
                slate
                :label="
                  $t('INTEGRATION_SETTINGS.VAULTSAGE.SETUP.ACTION_OPEN_INBOXES')
                "
                @click="goToInboxes"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="rounded-lg bg-n-alpha-2 p-4">
              <p class="text-xs uppercase tracking-wide text-n-slate-10 mb-1">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.NAME') }}
              </p>
              <p class="text-body-main text-n-slate-12">
                {{
                  connectedProfile.full_name ||
                  $t('INTEGRATION_SETTINGS.VAULTSAGE.UNKNOWN_USER')
                }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-2 p-4">
              <p class="text-xs uppercase tracking-wide text-n-slate-10 mb-1">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.EMAIL') }}
              </p>
              <p class="text-body-main text-n-slate-12">
                {{ connectedProfile.email || '-' }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-2 p-4 md:col-span-2">
              <p class="text-xs uppercase tracking-wide text-n-slate-10 mb-1">
                {{ $t('INTEGRATION_SETTINGS.VAULTSAGE.STATUS.BASE_URL') }}
              </p>
              <p class="text-body-main text-n-slate-12 break-all">
                {{ connectedProfile.base_url || formState.baseUrl }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
